// =============================================================================
// ui/state/useGoalCatalogue.ts — the goals list, for the editor.
// -----------------------------------------------------------------------------
// A HOOK, NOT A PROVIDER, and deliberately NOT `useGoals`.
//
// `useGoals` issues a SECOND read — every trade back to the widest goal's window
// — because the Dashboard card draws progress bars. The editor draws names. On a
// book with an all-time goal that second read is the whole trade history, and
// paying it to open Settings would be the largest read in the client issued on
// behalf of a screen with no numbers on it.
//
// ⚠ NOTHING INVALIDATES THE DASHBOARD after an edit here, and nothing needs to:
// `AppShell` remounts the tab subtree on navigation, so walking back to the
// Dashboard re-reads. A shared cache would need invalidating; two independent
// reads do not.
// =============================================================================

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSupabase } from './supabase';
import { loadGoals } from '../../io/sync/goals';
import type { Goal } from '../../selectors/goals';
import { reportFailure } from '../../io/diagnostics/errorLog';

export type GoalCataloguePhase = 'loading' | 'ready' | 'failed';

export interface GoalCatalogueState {
  phase: GoalCataloguePhase;
  /** ⚠ NULL until a read SUCCEEDED. An empty array means "you have set none". */
  goals: readonly Goal[] | null;
  error: string | null;
  reload: () => void;
}

export function useGoalCatalogue(): GoalCatalogueState {
  const { sb } = useSupabase();
  const [phase, setPhase] = useState<GoalCataloguePhase>('loading');
  const [goals, setGoals] = useState<readonly Goal[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);

  const reload = useCallback(() => setNonce((n) => n + 1), []);

  useEffect(() => {
    let cancelled = false;
    setPhase('loading');
    setError(null);

    void (async () => {
      try {
        const rows = await loadGoals(sb);
        if (cancelled) return;
        setGoals(rows);
        setPhase('ready');
      } catch (e) {
        if (cancelled) return;
        // ⚠ The list stays NULL rather than emptying. "No goals yet" beside a
        // "+ New Goal" button is an invitation to re-create targets that already
        // exist and are merely unreadable right now.
        setError(reportFailure('goal catalogue', e));
        setPhase('failed');
      }
    })();

    return () => { cancelled = true; };
  }, [sb, nonce]);

  return useMemo(
    () => ({ phase, goals, error, reload }),
    [phase, goals, error, reload],
  );
}
